(function (root, factory) {
  const model = typeof module === 'object' && module.exports
    ? require('./dim-text-model.js')
    : root.DIMTextModel;
  const api = factory(model);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DIMTextFonts = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, (model) => {
  'use strict';

  const HASH = /^sha256:[a-f0-9]{64}$/i;
  const FONT_ID = /^[a-z0-9][a-z0-9-]{0,63}$/;
  const FONT_FORMATS = new Set(['woff2', 'woff', 'truetype', 'opentype']);

  const cloneAndFreeze = (value) => {
    if (Array.isArray(value)) return Object.freeze(value.map(cloneAndFreeze));
    if (value && typeof value === 'object') {
      const copy = {};
      for (const [key, item] of Object.entries(value)) copy[key] = cloneAndFreeze(item);
      return Object.freeze(copy);
    }
    return value;
  };

  const stableJson = (value) => {
    if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
    if (value && typeof value === 'object') {
      return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`).join(',')}}`;
    }
    return JSON.stringify(value);
  };

  const canonicalManifestPayload = (manifest) => {
    if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) throw new Error('Invalid font manifest.');
    const payload = {};
    for (const [key, value] of Object.entries(manifest)) {
      if (key === 'manifestRevision') continue;
      if (key === 'fonts' && value && typeof value === 'object') {
        payload.fonts = Object.fromEntries(Object.entries(value).map(([id, entry]) => {
          const font = { ...entry };
          delete font.url;
          return [id, font];
        }));
      } else payload[key] = value;
    }
    return stableJson(payload);
  };

  const exactFields = (value, required, optional, label) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`Invalid ${label}.`);
    const allowed = new Set([...required, ...optional]);
    for (const field of Object.keys(value)) {
      if (!allowed.has(field)) throw new Error(`Unknown ${label} field: ${field}.`);
    }
    for (const field of required) {
      if (!Object.hasOwn(value, field)) throw new Error(`Missing ${label} field: ${field}.`);
    }
  };

  const nonempty = (value, field, maximum = 256) => {
    if (typeof value !== 'string' || !value.trim() || value.length > maximum) throw new Error(`Invalid ${field}.`);
    return value;
  };

  const normaliseCoverage = (coverage) => {
    exactFields(coverage, ['ranges'], ['sequences'], 'font coverage');
    if (!Array.isArray(coverage.ranges)) throw new Error('Invalid font coverage ranges.');
    const ranges = coverage.ranges.map((range) => {
      if (!Array.isArray(range) || range.length !== 2
        || !Number.isSafeInteger(range[0]) || !Number.isSafeInteger(range[1])
        || range[0] < 0 || range[1] > 0x10ffff || range[0] > range[1]) {
        throw new Error('Invalid font coverage range.');
      }
      return Object.freeze([range[0], range[1]]);
    }).sort((left, right) => left[0] - right[0]);
    for (let index = 1; index < ranges.length; index += 1) {
      if (ranges[index][0] <= ranges[index - 1][1]) throw new Error('Font coverage ranges must not overlap.');
    }
    const sequences = coverage.sequences === undefined ? [] : coverage.sequences;
    if (!Array.isArray(sequences) || sequences.some((item) => typeof item !== 'string' || !item)) {
      throw new Error('Invalid font coverage sequences.');
    }
    return Object.freeze({ ranges: Object.freeze(ranges), sequences: Object.freeze([...new Set(sequences)]) });
  };

  const validateApprovedManifest = (input) => {
    exactFields(input, ['schemaVersion', 'manifestRevision', 'approval', 'fonts'], [], 'font manifest');
    if (input.schemaVersion !== 'dim-font-manifest@1') throw new Error('Unsupported font manifest schema.');
    if (typeof input.manifestRevision !== 'string' || !HASH.test(input.manifestRevision)) {
      throw new Error('Invalid font manifest revision.');
    }
    exactFields(input.approval, ['status', 'authority', 'approvedAt', 'scope'], [], 'font approval');
    if (input.approval.status !== 'approved') throw new Error('The font manifest is not approved.');
    for (const field of ['authority', 'approvedAt', 'scope']) nonempty(input.approval[field], `font approval ${field}`);
    if (!input.fonts || typeof input.fonts !== 'object' || Array.isArray(input.fonts)) {
      throw new Error('Invalid font manifest fonts.');
    }
    const fonts = {};
    for (const [id, entry] of Object.entries(input.fonts)) {
      if (!FONT_ID.test(id)) throw new Error('Invalid font ID.');
      exactFields(entry, [
        'family', 'label', 'weight', 'style', 'revision', 'asset', 'format', 'sha256', 'coverage'
      ], ['weightRange', 'url'], 'font');
      if (!Number.isSafeInteger(entry.weight) || entry.weight < 1 || entry.weight > 1000) {
        throw new Error('Invalid font weight.');
      }
      if (entry.style !== 'normal') throw new Error('Unsupported font style.');
      let weightRange = null;
      if (entry.weightRange !== undefined) {
        if (!Array.isArray(entry.weightRange) || entry.weightRange.length !== 2
          || !entry.weightRange.every((value) => Number.isSafeInteger(value) && value >= 1 && value <= 1000)
          || entry.weightRange[0] > entry.weightRange[1]
          || entry.weight < entry.weightRange[0] || entry.weight > entry.weightRange[1]) {
          throw new Error('Invalid variable font weight range.');
        }
        weightRange = Object.freeze([...entry.weightRange]);
      }
      if (typeof entry.revision !== 'string' || !HASH.test(entry.revision)) throw new Error('Invalid font revision.');
      if (typeof entry.sha256 !== 'string' || !HASH.test(entry.sha256)) throw new Error('Invalid font byte hash.');
      if (entry.revision.toLowerCase() !== entry.sha256.toLowerCase()) {
        throw new Error('Font revision must identify the exact approved bytes.');
      }
      if (!FONT_FORMATS.has(entry.format)) throw new Error('Unsupported font format.');
      if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(entry.asset) || entry.asset.includes('..')) {
        throw new Error('Invalid font asset filename.');
      }
      fonts[id] = Object.freeze({
        family: nonempty(entry.family, 'font family'),
        label: nonempty(entry.label, 'font label'),
        weight: entry.weight,
        style: entry.style,
        revision: entry.revision.toLowerCase(),
        asset: entry.asset,
        ...(entry.url === undefined ? {} : { url: nonempty(entry.url, 'font URL', 2048) }),
        format: entry.format,
        sha256: entry.sha256.toLowerCase(),
        ...(weightRange ? { weightRange } : {}),
        coverage: normaliseCoverage(entry.coverage)
      });
    }
    return cloneAndFreeze({
      schemaVersion: input.schemaVersion,
      manifestRevision: input.manifestRevision.toLowerCase(),
      approval: input.approval,
      fonts
    });
  };

  const codePointCovered = (entry, codePoint) => entry.coverage.ranges.some(
    ([minimum, maximum]) => codePoint >= minimum && codePoint <= maximum
  );

  const assertGlyphCoverage = (entry, text) => {
    if (!entry || !entry.coverage) throw new Error('Approved font coverage is unavailable.');
    if (typeof text !== 'string') throw new TypeError('Text must be a string.');
    // Inspect line breaks canonically: Intl groups CRLF as one grapheme.
    // This is only a coverage view; retained wording is never rewritten here.
    for (const grapheme of model.graphemes(model.normaliseLineBreaks(text))) {
      if (grapheme === '\n' || grapheme === '\r') continue;
      if (entry.coverage.sequences.includes(grapheme)) continue;
      if (![...grapheme].every((character) => codePointCovered(entry, character.codePointAt(0)))) {
        throw new Error(`The selected font does not support ${JSON.stringify(grapheme)}.`);
      }
    }
    return true;
  };

  const bytesToHex = (bytes) => [...new Uint8Array(bytes)]
    .map((value) => value.toString(16).padStart(2, '0')).join('');

  const createFontLoader = (inputManifest, {
    resolveAssetUrl,
    allowedOrigins = [],
    baseUrl = typeof document !== 'undefined' ? document.baseURI : undefined,
    fetchFn = typeof fetch === 'function' ? fetch.bind(globalThis) : null,
    cryptoApi = typeof crypto !== 'undefined' ? crypto : null,
    FontFaceClass = typeof FontFace === 'function' ? FontFace : null,
    fontSet = typeof document !== 'undefined' ? document.fonts : null,
    timeoutMs = 8000
  } = {}) => {
    const manifestPayload = canonicalManifestPayload(inputManifest);
    const manifest = validateApprovedManifest(inputManifest);
    const pending = new Map();
    const loaded = new Map();
    const assetUrl = typeof resolveAssetUrl === 'function'
      ? resolveAssetUrl
      : (_asset, entry) => entry.url;
    if (typeof assetUrl !== 'function') throw new Error('A trusted font asset URL resolver is required.');
    if (!Array.isArray(allowedOrigins)) throw new Error('Invalid allowed font origins.');
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) throw new Error('Invalid font timeout.');

    let verifiedManifest = null;
    const verifyManifest = async () => {
      if (!verifiedManifest) {
        verifiedManifest = (async () => {
          if (!cryptoApi?.subtle || typeof TextEncoder !== 'function') {
            throw new Error('Font manifest integrity verification is unavailable.');
          }
          const bytes = new TextEncoder().encode(manifestPayload);
          const digest = await cryptoApi.subtle.digest('SHA-256', bytes);
          if (`sha256:${bytesToHex(digest)}` !== manifest.manifestRevision) {
            throw new Error('The font manifest failed its integrity check.');
          }
          return true;
        })();
        verifiedManifest.catch(() => { verifiedManifest = null; });
      }
      return verifiedManifest;
    };

    const ensure = async (id, revision, { text = '' } = {}) => {
      const entry = Object.hasOwn(manifest.fonts, id) ? manifest.fonts[id] : null;
      if (!entry) throw new Error('Unknown font.');
      if (entry.revision !== String(revision || '').toLowerCase()) throw new Error('Font revision no longer available.');
      assertGlyphCoverage(entry, text);
      if (typeof fetchFn !== 'function' || !cryptoApi?.subtle || typeof FontFaceClass !== 'function' || !fontSet?.add) {
        throw new Error('Verified custom fonts are unavailable in this browser.');
      }
      await verifyManifest();
      const range = entry.weightRange ? entry.weightRange.join('-') : String(entry.weight);
      const key = `${entry.family}:${entry.sha256}:${range}:${entry.style}`;
      if (loaded.has(key)) return loaded.get(key);
      if (!pending.has(key)) {
        const job = (async () => {
          const resolved = assetUrl(entry.asset, entry);
          if (typeof resolved !== 'string' || !resolved) throw new Error('A trusted font asset URL is required.');
          const url = new URL(resolved, baseUrl);
          if (url.protocol !== 'https:' || url.username || url.password || !allowedOrigins.includes(url.origin)) {
            throw new Error('Unapproved font origin.');
          }
          const controller = typeof AbortController === 'function' ? new AbortController() : null;
          let timer;
          const timeout = new Promise((_, reject) => {
            timer = setTimeout(() => {
              controller?.abort();
              reject(new Error('The font timed out. Retry or choose another font.'));
            }, timeoutMs);
          });
          try {
            const response = await Promise.race([fetchFn(url.href, controller ? { signal: controller.signal } : {}), timeout]);
            if (!response?.ok || typeof response.arrayBuffer !== 'function') throw new Error('The font asset could not be loaded.');
            const bytes = await Promise.race([response.arrayBuffer(), timeout]);
            const digest = await cryptoApi.subtle.digest('SHA-256', bytes);
            if (`sha256:${bytesToHex(digest)}` !== entry.sha256) {
              throw new Error('The font asset failed its integrity check.');
            }
            const face = new FontFaceClass(entry.family, bytes, {
              weight: entry.weightRange ? `${entry.weightRange[0]} ${entry.weightRange[1]}` : String(entry.weight),
              style: entry.style
            });
            const ready = await Promise.race([face.load(), timeout]);
            if (!ready || ready.status !== 'loaded') throw new Error('The exact font face was not loaded.');
            fontSet.add(ready);
            loaded.set(key, ready);
            return ready;
          } finally {
            clearTimeout(timer);
          }
        })();
        pending.set(key, job);
        job.finally(() => pending.delete(key)).catch(() => {});
      }
      return pending.get(key);
    };

    const rendererCatalog = Object.freeze(Object.fromEntries(Object.entries(manifest.fonts).map(([id, entry]) => [id, Object.freeze({
      family: entry.family,
      weight: entry.weight,
      style: entry.style,
      revision: entry.revision
    })])));

    return Object.freeze({ manifest, rendererCatalog, ensure, assertGlyphCoverage: (id, text) => {
      const entry = manifest.fonts[id];
      if (!entry) throw new Error('Unknown font.');
      return assertGlyphCoverage(entry, text);
    } });
  };

  return Object.freeze({ canonicalManifestPayload, validateApprovedManifest, assertGlyphCoverage, createFontLoader });
}));
